/**
 * Route configuration for the application.
 * This file is the single source of truth for all routes.
 * Components are lazy-loaded for code-splitting.
 */

import { ComponentType, lazy, MutableRefObject } from "react";
import { MapRef } from "react-map-gl/mapbox";

export type UserType = "farmer" | "corporate" | "all";

/**
 * Initialization type for routes - determines how farm context is set up
 * - farm: Initialize by fetching farm from :farmId param
 * - field: Initialize by fetching field from :fieldId param, then its farm
 * - animalGroup: Initialize by fetching animal group from :animalGroupId param, then its farm
 * - defaultFarm: Initialize with user's first/default farm
 * - addFarm: Special handling for add-farm flow (may or may not have farms)
 * - none: No initialization needed (oauth callbacks, etc.)
 */
export type InitializeWith =
  | "farm"
  | "field"
  | "animalGroup"
  | "defaultFarm"
  | "addFarm"
  | "none";

/**
 * Layout types for routes
 * - panel: Secondary panel next to the drawer (400px width, for farm/field views)
 * - full-screen: Full width panel covering map area
 * - bottom-sheet: Mobile bottom sheet with routing (farm tab routes)
 * - none: No layout wrapper
 */
export type LayoutType = "panel" | "full-screen" | "bottom-sheet" | "none";

/**
 * Props that can be passed to route components
 */
export interface RouteComponentProps {
  mapRef?: MutableRefObject<MapRef | null>;
}

export interface RouteConfig {
  path: string;
  // Lazy-loaded component for this route
  component: React.LazyExoticComponent<ComponentType<RouteComponentProps>>;
  // Mobile-specific component (optional, falls back to component)
  mobileComponent?: React.LazyExoticComponent<
    ComponentType<RouteComponentProps>
  >;
  // Layout configuration
  layout: {
    desktop: LayoutType;
    mobile: LayoutType;
  };
  // Whether to show the map behind the content
  showMap: boolean;
  // Whether to show the map on mobile (defaults to showMap if not specified)
  showMapMobile?: boolean;
  // Whether this route requires authentication
  requiresAuth: boolean;
  // Which user types can access this route
  userType: UserType;
  // Whether this is a "farm tab" route (for bottom nav highlighting)
  isFarmTab?: boolean;
  // Whether component needs mapRef prop
  needsMapRef?: boolean;
  // Whether to use white background (for panel layout)
  whiteBackground?: boolean;
  // Whether this route has special desktop handling (not rendered by RouteRenderer)
  hasSpecialDesktopHandling?: boolean;
  // Map interaction flags
  // Whether this route enables field drawing/editing mode on the map
  isFieldEditMode?: boolean;
  // Whether to disable map click interactions (clicking fields to navigate)
  disableMapClick?: boolean;
  // Whether this is a "map route" (drawer should have transparent background)
  // Used for routes where the map is visible behind the content panel
  isMapRoute?: boolean;
  // How to initialize farm context for this route
  // Defaults to "defaultFarm" if not specified
  initializeWith?: InitializeWith;
}

// ===== LAZY COMPONENT IMPORTS =====

// Farm/Field components
const FarmView = lazy(() => import("features/farmOverview/FarmView"));
const FarmInput = lazy(() =>
  import("features/farmOverview/FarmInput").then((m) => ({
    default: m.FarmInput,
  }))
);
const FieldView = lazy(() => import("features/fieldDetail/FieldView"));
const FieldInput = lazy(() => import("features/fieldEdit/FieldInput"));
const FieldsListScreen = lazy(
  () => import("features/farmOverview/FieldsListScreen")
);

// Animal group components
const AnimalGroupDetail = lazy(
  () => import("features/animalGroupDetail/AnimalGroupDetail")
);
const AnimalGroupInput = lazy(
  () => import("features/animalGroupDetail/AnimalGroupInput")
);

// Cover components
const Covers = lazy(() =>
  import("features/bulkCoverInput").then((m) => ({ default: m.Covers }))
);

// Insights components
const FarmInsights = lazy(() =>
  import("features/insights/FarmInsights").then((m) => ({
    default: m.FarmInsights,
  }))
);
const FarmInsightsDrawer = lazy(
  () => import("features/insights/FarmInsightsDrawer")
);
const ProjectInsights = lazy(() =>
  import("features/insights/ProjectInsights").then((m) => ({
    default: m.ProjectInsights,
  }))
);
const ProjectInsightsDrawer = lazy(
  () => import("features/insights/ProjectsInsightsDrawer")
);
const ProjectInsightsMetrics = lazy(() =>
  import("features/insights/ProjectInsightsMetrics").then((m) => ({
    default: m.ProjectInsightsMetrics,
  }))
);
const FarmInsightMetrics = lazy(() =>
  import("features/insights/FarmInsightMetrics").then((m) => ({
    default: m.FarmInsightMetrics,
  }))
);
const AccessibleFarmsInsights = lazy(() =>
  import("features/insights/AccessibleFarmsInsights").then((m) => ({
    default: m.AccessibleFarmsInsights,
  }))
);
const AccessibleFarmsInsightsDrawer = lazy(
  () => import("features/insights/AccessibleFarmsInsightsDrawer")
);
// Programmes components
const ProgrammesList = lazy(
  () => import("features/programmesActions/ProgrammesList")
);
const ProgrammesDrawer = lazy(
  () => import("features/programmesActions/ProgrammesDrawer")
);
const ProgrammeDetailsWeb = lazy(
  () => import("features/programmesActions/ProgrammeDetailsWeb")
);
const ProgrammeDetailsDrawer = lazy(
  () => import("features/programmesActions/ProgrammesDetailsDrawer")
);
const ActionDetails = lazy(
  () => import("features/programmesActions/ActionDetails")
);

// Planner components
const Planner = lazy(() =>
  import("features/planner").then((m) => ({ default: m.Planner }))
);
const PlannerDrawer = lazy(() =>
  import("features/planner").then((m) => ({ default: m.PlannerDrawer }))
);

// File management components
const FileManagement = lazy(
  () => import("features/fileManagement/FileManagement")
);
const FileEntryDetail = lazy(
  () => import("features/fileManagement/FileManagementDetail")
);
const FileDownload = lazy(() => import("features/fileManagement/FileDownload"));
const AddNewFile = lazy(() => import("features/fileManagement/AddNewFile"));

// Inputs/Outputs components
const CombinedInputsOutputsDetail = lazy(() =>
  import("features/combinedInputsOutputs").then((m) => ({
    default: m.CombinedInputsOutputsDetail,
  }))
);

// ===== ROUTE DEFINITIONS =====

/**
 * All application routes with their metadata and components.
 * This is the single source of truth for routing behavior.
 */
export const routes: RouteConfig[] = [
  // ===== FARMER ROUTES - FARM TAB =====
  {
    path: "/farm/:farmId",
    component: FarmView,
    layout: { desktop: "panel", mobile: "bottom-sheet" },
    showMap: true,
    requiresAuth: true,
    userType: "farmer",
    isFarmTab: true,
    isMapRoute: true,
    initializeWith: "farm",
  },
  {
    path: "/add-farm/*",
    component: FarmInput,
    layout: { desktop: "panel", mobile: "bottom-sheet" },
    showMap: true,
    requiresAuth: true,
    userType: "farmer",
    isFarmTab: true,
    needsMapRef: true,
    whiteBackground: true,
    disableMapClick: true,
    isMapRoute: true,
    initializeWith: "addFarm",
  },
  {
    path: "/field/:fieldId",
    component: FieldView,
    layout: { desktop: "panel", mobile: "bottom-sheet" },
    showMap: true,
    showMapMobile: true,
    requiresAuth: true,
    userType: "farmer",
    isFarmTab: true,
    whiteBackground: true,
    isMapRoute: true,
    initializeWith: "field",
  },
  {
    path: "/add-field/*",
    component: FieldInput,
    layout: { desktop: "panel", mobile: "bottom-sheet" },
    showMap: true,
    requiresAuth: true,
    userType: "farmer",
    isFarmTab: true,
    whiteBackground: true,
    isFieldEditMode: true,
    isMapRoute: true,
    initializeWith: "defaultFarm",
  },
  {
    path: "/edit-field/:fieldId/*",
    component: FieldInput,
    layout: { desktop: "panel", mobile: "bottom-sheet" },
    showMap: true,
    requiresAuth: true,
    userType: "farmer",
    isFarmTab: true,
    whiteBackground: true,
    isFieldEditMode: true,
    isMapRoute: true,
    initializeWith: "field",
  },
  {
    path: "/fields-list/:fieldType",
    component: FieldsListScreen,
    layout: { desktop: "panel", mobile: "bottom-sheet" },
    showMap: true,
    showMapMobile: true,
    requiresAuth: true,
    userType: "farmer",
    isFarmTab: true,
    isMapRoute: true,
    initializeWith: "defaultFarm",
  },
  {
    path: "/animal-group/:animalGroupId/*",
    component: AnimalGroupDetail,
    layout: { desktop: "panel", mobile: "bottom-sheet" },
    showMap: true,
    showMapMobile: true,
    requiresAuth: true,
    userType: "farmer",
    isFarmTab: true,
    whiteBackground: true,
    isMapRoute: true,
    initializeWith: "animalGroup",
  },
  {
    path: "/add-animal-group",
    component: AnimalGroupInput,
    layout: { desktop: "panel", mobile: "bottom-sheet" },
    showMap: true,
    showMapMobile: true,
    requiresAuth: true,
    userType: "farmer",
    isFarmTab: true,
    whiteBackground: true,
    isMapRoute: true,
    initializeWith: "defaultFarm",
  },
  {
    path: "/edit-animal-group/:animalGroupId",
    component: AnimalGroupInput,
    layout: { desktop: "panel", mobile: "bottom-sheet" },
    showMap: true,
    showMapMobile: true,
    requiresAuth: true,
    userType: "farmer",
    isFarmTab: true,
    whiteBackground: true,
    isMapRoute: true,
    initializeWith: "animalGroup",
  },
  {
    path: "/add-covers/*",
    component: Covers,
    layout: { desktop: "none", mobile: "bottom-sheet" },
    showMap: true,
    requiresAuth: true,
    userType: "farmer",
    isFarmTab: true,
    isMapRoute: true,
    initializeWith: "defaultFarm",
  },

  // ===== FARMER ROUTES - FULL SCREEN =====
  {
    path: "/records",
    component: CombinedInputsOutputsDetail,
    layout: { desktop: "full-screen", mobile: "full-screen" },
    showMap: false,
    requiresAuth: true,
    userType: "farmer",
    disableMapClick: true,
    initializeWith: "defaultFarm",
  },
  {
    path: "/planner",
    component: Planner,
    mobileComponent: PlannerDrawer,
    layout: { desktop: "full-screen", mobile: "full-screen" },
    showMap: false,
    requiresAuth: true,
    userType: "farmer",
    initializeWith: "defaultFarm",
  },
  {
    path: "/programmes",
    component: ProgrammesList,
    mobileComponent: ProgrammesDrawer,
    layout: { desktop: "full-screen", mobile: "full-screen" },
    showMap: false,
    requiresAuth: true,
    userType: "farmer",
    initializeWith: "defaultFarm",
  },
  {
    path: "/programme-details/:programmeId",
    component: ProgrammeDetailsWeb,
    mobileComponent: ProgrammeDetailsDrawer,
    layout: { desktop: "full-screen", mobile: "full-screen" },
    showMap: false,
    requiresAuth: true,
    userType: "farmer",
    hasSpecialDesktopHandling: true,
    initializeWith: "defaultFarm",
  },
  {
    path: "/action-details/:id",
    component: ActionDetails,
    layout: { desktop: "full-screen", mobile: "full-screen" },
    showMap: false,
    requiresAuth: true,
    userType: "farmer",
    initializeWith: "defaultFarm",
  },
  {
    path: "/farm-insights",
    component: FarmInsights,
    mobileComponent: FarmInsightsDrawer,
    layout: { desktop: "full-screen", mobile: "full-screen" },
    showMap: false,
    requiresAuth: true,
    userType: "farmer",
    hasSpecialDesktopHandling: true,
    initializeWith: "defaultFarm",
  },
  {
    path: "/farm-insight-metrics",
    component: FarmInsightMetrics,
    layout: { desktop: "full-screen", mobile: "full-screen" },
    showMap: false,
    requiresAuth: true,
    userType: "all",
    initializeWith: "defaultFarm",
  },
  // ===== CORPORATE ROUTES =====
  {
    path: "/project-insights",
    component: ProjectInsights,
    mobileComponent: ProjectInsightsDrawer,
    layout: { desktop: "full-screen", mobile: "full-screen" },
    showMap: false,
    requiresAuth: true,
    userType: "corporate",
    hasSpecialDesktopHandling: true,
    initializeWith: "none",
  },
  {
    path: "/project-insights-metrics",
    component: ProjectInsightsMetrics,
    layout: { desktop: "full-screen", mobile: "full-screen" },
    showMap: false,
    requiresAuth: true,
    userType: "corporate",
    initializeWith: "none",
  },
  {
    path: "/accessible-farms-insights",
    component: AccessibleFarmsInsights,
    mobileComponent: AccessibleFarmsInsightsDrawer,
    layout: { desktop: "full-screen", mobile: "full-screen" },
    showMap: false,
    requiresAuth: true,
    userType: "corporate",
    hasSpecialDesktopHandling: true,
    initializeWith: "none",
  },

  // ===== SHARED ROUTES (ALL USERS) =====
  {
    path: "/file-management-overview",
    component: FileManagement,
    mobileComponent: FileEntryDetail,
    layout: { desktop: "panel", mobile: "full-screen" },
    showMap: false,
    requiresAuth: true,
    userType: "all",
    whiteBackground: true,
    initializeWith: "defaultFarm",
  },
  {
    path: "/file-download/:fileEntryId/:goBackParam",
    component: FileDownload,
    layout: { desktop: "none", mobile: "none" },
    showMap: false,
    requiresAuth: true,
    userType: "all",
    initializeWith: "defaultFarm",
  },
  {
    path: "/add-new-file",
    component: AddNewFile,
    layout: { desktop: "none", mobile: "none" },
    showMap: false,
    requiresAuth: true,
    userType: "all",
    initializeWith: "defaultFarm",
  },
];

/**
 * Navigation tab configuration for bottom navigation on mobile
 */
export interface NavTab {
  id: string;
  labelKey: string;
  // Function to get the route path (may depend on farmId)
  getPath: (farmId?: string) => string;
  analyticsEvent: string;
  // Icon component name (will be resolved in BottomTabNavigation)
  iconName: string;
  // Whether this is the "add" button (special handling)
  isAddButton?: boolean;
}

export const navigationConfig: Record<
  "farmer" | "corporate",
  {
    tabs: NavTab[];
    getDefaultRoute: (farmId?: string) => string;
  }
> = {
  farmer: {
    tabs: [
      {
        id: "farm",
        labelKey: "go.to.landmanagement.and.grazing.button",
        getPath: (farmId) =>
          farmId ? `/farm/${farmId}` : "/add-farm/enter-name",
        analyticsEvent: "Go to farm view clicked",
        iconName: "FarmIcon",
      },
      {
        id: "records",
        labelKey: "go.to.records",
        getPath: () => "/records",
        analyticsEvent: "Go to records clicked",
        iconName: "RecordsIcon",
      },
      {
        id: "add",
        labelKey: "",
        getPath: () => "add",
        analyticsEvent: "Add button clicked",
        iconName: "AddButtonIcon",
        isAddButton: true,
      },
      {
        id: "planner",
        labelKey: "go.to.planner",
        getPath: () => "/planner",
        analyticsEvent: "Go to planner clicked",
        iconName: "ProgrammesIcon",
      },
      {
        id: "farm-insights",
        labelKey: "go.to.farm.insights",
        getPath: () => "/farm-insights",
        analyticsEvent: "Go to farm insights clicked",
        iconName: "InsightsIcon",
      },
    ],
    getDefaultRoute: (farmId) =>
      farmId ? `/farm/${farmId}` : "/add-farm/enter-name",
  },
  corporate: {
    tabs: [
      {
        id: "accessible-farms-insights",
        labelKey: "go.to.accessible.farms.insights",
        getPath: () => "/accessible-farms-insights",
        analyticsEvent: "Go to farms insights clicked",
        iconName: "InsightsIcon",
      },
      {
        id: "project-insights",
        labelKey: "go.to.project.insights.mobile",
        getPath: () => "/project-insights",
        analyticsEvent: "Go to farms insights clicked",
        iconName: "ProgrammesIcon",
      },
      {
        id: "file-management",
        labelKey: "go.to.file.management.button.mobile",
        getPath: () => "/file-management-overview",
        analyticsEvent: "Go to file management view clicked",
        iconName: "FolderIcon",
      },
      {
        id: "profile",
        labelKey: "settings.title",
        getPath: () => "/profile",
        analyticsEvent: "Go to profile view clicked",
        iconName: "ProfileIcon",
      },
    ],
    getDefaultRoute: () => "/project-insights",
  },
};

/**
 * Get the default route for a user after login or page refresh
 */
export function getDefaultRoute(
  userType: "farmer" | "corporate",
  farmId?: string
): string {
  return navigationConfig[userType].getDefaultRoute(farmId);
}

/**
 * Find route config by path
 */
export function findRouteByPath(pathname: string): RouteConfig | undefined {
  return routes.find((route) => {
    // Handle wildcard routes (ending with /*)
    const routePath = route.path.replace(/\/\*$/, "");
    const hasWildcard = route.path.endsWith("/*");

    // Simple path matching - for dynamic routes, check if pattern matches
    if (routePath.includes(":")) {
      const pattern = routePath.replace(/:[^/]+/g, "[^/]+");
      const regex = hasWildcard
        ? new RegExp(`^${pattern}(/.*)?$`)
        : new RegExp(`^${pattern}$`);
      return regex.test(pathname);
    }

    // For static paths
    return hasWildcard
      ? pathname.startsWith(routePath)
      : pathname === routePath;
  });
}

/**
 * Get routes filtered by layout type and platform
 */
export function getRoutesByLayout(
  layoutType: LayoutType,
  platform: "desktop" | "mobile"
): RouteConfig[] {
  return routes.filter((route) => {
    const layout =
      platform === "mobile" ? route.layout.mobile : route.layout.desktop;
    return layout === layoutType;
  });
}

/**
 * Get farm tab routes (routes that render in the bottom sheet on mobile)
 */
export function getFarmTabRoutes(): RouteConfig[] {
  return routes.filter((route) => route.isFarmTab);
}

/**
 * Check if a route should show the map
 */
export function shouldShowMap(
  pathname: string,
  isMobile: boolean = false
): boolean {
  const route = findRouteByPath(pathname);
  if (!route) return false;
  if (isMobile && route.showMapMobile !== undefined) {
    return route.showMapMobile;
  }
  return route.showMap;
}

/**
 * Check if a path is a "farm tab" route (for bottom nav highlighting)
 */
export function isFarmTabRoute(pathname: string): boolean {
  const route = findRouteByPath(pathname);
  return route?.isFarmTab ?? false;
}

/**
 * Filter routes by user type
 */
export function getRoutesForUserType(
  userType: "farmer" | "corporate"
): RouteConfig[] {
  return routes.filter(
    (route) => route.userType === "all" || route.userType === userType
  );
}

/**
 * Extract field ID from pathname if viewing a field
 * Returns the fieldId for /field/:fieldId route only
 */
export function extractViewedFieldIdFromPath(pathname: string): string | null {
  const fieldMatch = pathname.match(/^\/field\/([^/]+)/);
  return fieldMatch ? fieldMatch[1] : null;
}

/**
 * Extract field ID from pathname if editing a field
 * Returns the fieldId for /edit-field/:fieldId route only
 */
export function extractEditedFieldIdFromPath(pathname: string): string | null {
  const editMatch = pathname.match(/^\/edit-field\/([^/]+)/);
  return editMatch ? editMatch[1] : null;
}

/**
 * Get layout state for a given path
 * Returns flags for drawer styling (isMapRoute, isHomeOrFarm)
 */
export function getLayoutStateForPath(pathname: string): {
  isMapRoute: boolean;
  isHomeOrFarm: boolean;
} {
  const route = findRouteByPath(pathname);

  // Check if this is home (/) or farm route
  const isHomeOrFarm = pathname === "/" || pathname.startsWith("/farm/");

  return {
    isMapRoute: route?.isMapRoute ?? false,
    isHomeOrFarm,
  };
}

/**
 * Get map interaction state for a given path
 * Returns flags indicating whether to show map, enable edit mode, disable clicks, etc.
 */
export function getMapStateForPath(pathname: string): {
  showMap: boolean;
  isFieldEditMode: boolean;
  disableMapClick: boolean;
  selectedFieldId: string | null;
  editedFieldId: string | null;
} {
  const route = findRouteByPath(pathname);

  // Public routes (login, signup) - hide map entirely
  const publicRoutes = ["/login", "/signup", "/signup-intro"];
  const isPublicRoute = publicRoutes.some(
    (r) => pathname === r || pathname.startsWith(r + "/")
  );

  if (isPublicRoute) {
    return {
      showMap: false,
      isFieldEditMode: false,
      disableMapClick: true,
      selectedFieldId: null,
      editedFieldId: null,
    };
  }

  return {
    showMap: route?.showMap ?? true,
    isFieldEditMode: route?.isFieldEditMode ?? false,
    disableMapClick: route?.disableMapClick ?? false,
    selectedFieldId: extractViewedFieldIdFromPath(pathname),
    editedFieldId: extractEditedFieldIdFromPath(pathname),
  };
}
